/* src/pages/Dashboard.jsx
 * Today operator dashboard (/dashboard).
 * Zero forms → OnboardingChecklist (new-user path).
 * ≥1 form → Today view fed by getWorkspaceConversations.
 */

import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { leadApi } from '../utils/api';
import { getUserWorkspaceAccess } from '../utils/accessControl';
import OnboardingChecklist from '../components/OnboardingChecklist';
import TodayView from '../components/today/TodayView';
import { TodayPageSkeleton } from '../components/Skeleton';


export default function Dashboard() {
  const { currentUser, userProfile } = useAuth();
  const { selectedWorkspaceId, loading: workspaceLoading } = useWorkspace();
  const isMobile = useIsMobile();

  const [forms, setForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [callBackRows, setCallBackRows] = useState([]);
  const [callBackLoading, setCallBackLoading] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  const isOnboarded = forms.length >= 1;

  useEffect(() => {
    if (!currentUser) {
      setForms([]);
      setLoadingForms(false);
      return undefined;
    }
    if (!selectedWorkspaceId) {
      setForms([]);
      // Workspace still resolving — keep the skeleton so a set-up account
      // does not flash the zero-form onboarding checklist before Today.
      setLoadingForms(workspaceLoading);
      return undefined;
    }

    setLoadingForms(true);
    const q = query(collection(db, 'forms'), where('workspaceId', '==', selectedWorkspaceId));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setForms(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingForms(false);
      },
      () => {
        setForms([]);
        setLoadingForms(false);
      }
    );
    return () => unsub();
  }, [currentUser, selectedWorkspaceId, workspaceLoading]);

  useEffect(() => {
    if (!currentUser || !selectedWorkspaceId || !isOnboarded) {
      setConversations([]);
      setAccessDenied(false);
      setLoadError(false);
      setLoadingConversations(false);
      setCallBackRows([]);
      setCallBackLoading(false);
      setIsOwner(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingConversations(true);
    setCallBackLoading(true);
    setLoadError(false);
    setAccessDenied(false);

    async function load() {
      try {
        const access = await getUserWorkspaceAccess(currentUser.uid, selectedWorkspaceId);
        if (cancelled) return;
        setIsOwner(access.isOwner === true);
        if (!access.leadAccess && !access.isOwner) {
          setAccessDenied(true);
          setConversations([]);
          setCallBackRows([]);
          setLoadingConversations(false);
          setCallBackLoading(false);
          return;
        }

        const [result, callBack] = await Promise.all([
          leadApi.getWorkspaceConversations(selectedWorkspaceId),
          leadApi.getCallBackList(selectedWorkspaceId).catch(() => ({ rows: [] })),
        ]);
        if (cancelled) return;
        setConversations(result.conversations || []);
        setCallBackRows(callBack.rows || []);
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setConversations([]);
          setCallBackRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingConversations(false);
          setCallBackLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [currentUser, selectedWorkspaceId, isOnboarded]);

  if (workspaceLoading || loadingForms) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        <TodayPageSkeleton isMobile={isMobile} showHeader />
      </div>
    );
  }

  if (!isOnboarded) {
    return (
      <div
        className="console-shell"
        style={{
          minHeight: '100vh',
          background: 'var(--bg)',
          padding: isMobile ? '1rem' : '2rem',
          maxWidth: '900px',
          margin: '0 auto',
        }}
      >
        <OnboardingChecklist userProfile={userProfile} />
      </div>
    );
  }

  if (loadingConversations && !accessDenied) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        <div style={{ padding: isMobile ? '1rem' : '24px 32px' }}>
          <div className="today-h1" style={{ marginBottom: '1.5rem' }}>Today</div>
          <TodayPageSkeleton isMobile={isMobile} embedded />
        </div>
      </div>
    );
  }

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <TodayView
        conversations={conversations}
        userProfile={userProfile}
        isMobile={isMobile}
        accessDenied={accessDenied}
        loadError={loadError}
        firstForm={forms[0] || null}
        callBackRows={callBackRows}
        callBackLoading={callBackLoading}
        workspaceId={selectedWorkspaceId}
        isOwner={isOwner}
      />
    </div>
  );
}
